import { useEffect, useState } from "react";
import { entityHistory } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { History, Loader2 } from "lucide-react";

/**
 * Read-only timeline of every audit event for a single record.
 * Used inline from Sales / Leads / Customer drawers so operators can trace an edit
 * back to a person + timestamp without leaving the record.
 */
export function AuditHistoryDialog({ open, onClose, entityType, entityId, entityLabel }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !entityId) return;
    setLoading(true);
    entityHistory(entityType, entityId)
      .then((d) => setRows(d))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, entityType, entityId]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <History className="h-4 w-4 text-amber-400" />
            Change history
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {entityLabel ? `${entityLabel} · ` : ""}Every recorded edit, most recent first.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading history…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-xs text-zinc-500">No changes recorded yet.</div>
        ) : (
          <div className="space-y-3 mt-3">
            {rows.map((r) => (
              <div key={r.id} className="border border-[#262626] rounded-lg bg-[#0a0a0a] p-3" data-testid={`audit-history-row-${r.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Badge className={`border ${TONE[r.action] || TONE.update}`}>{r.action}</Badge>
                    <div className="text-xs text-zinc-300">
                      <span className="text-zinc-500">by</span> {r.actor_name || r.actor_email || "system"}
                    </div>
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500">{new Date(r.at).toLocaleString()}</div>
                </div>
                {(r.changed_fields || []).length > 0 && (
                  <table className="w-full text-[11px] mt-3">
                    <tbody>
                      {(r.changed_fields || []).map((f) => (
                        <tr key={f.field}>
                          <td className="text-zinc-400 font-mono pr-4 py-1 align-top w-40">{f.field}</td>
                          <td className="text-rose-300/90 font-mono pr-3 py-1 align-top">{fmt(f.old)}</td>
                          <td className="text-emerald-300/90 font-mono py-1 align-top">→ {fmt(f.new)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {r.note && !r.changed_fields?.length && (
                  <div className="text-[11px] text-zinc-500 mt-2">{r.note}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TONE = {
  create:      "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  update:      "bg-blue-500/10 text-blue-300 border-blue-500/30",
  delete:      "bg-rose-500/10 text-rose-300 border-rose-500/30",
  role_change: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  import:      "bg-lime-500/10 text-lime-300 border-lime-500/30",
  import_undo: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
};

function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 100);
  const s = String(v);
  return s.length > 100 ? s.slice(0, 100) + "…" : s;
}
